import { useCallback, useEffect, useMemo, useState } from 'react'
import { BookOpen, ChevronDown, Sparkles } from 'lucide-react'
import { BRIEF_SECTIONS, type MarketBrief, type BriefSection } from '@ispot/shared'

const SECTION_LABELS: Record<BriefSection, string> = {
  summary: 'Executive summary',
  definition: 'Segment definition',
  drivers: 'Demand drivers',
  growth: 'Growth outlook',
  competitive: 'Competitive landscape',
  buyers: 'Buying process',
  regulatory: 'Regulatory context',
  strategicFit: 'Strategic fit',
  risks: 'Risks & mitigations',
}

interface Props {
  segmentId: string
  onGenerate: () => Promise<MarketBrief>
  onList: () => Promise<MarketBrief[]>
}

export function SegmentBriefSection({ segmentId, onGenerate, onList }: Props) {
  const [briefs, setBriefs] = useState<MarketBrief[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await onList()
      setBriefs(list)
      const current = list.find((b) => b.isCurrent)
      setSelectedId(current?.id ?? list[list.length - 1]?.id ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load brief')
    } finally {
      setLoading(false)
    }
  }, [onList])

  useEffect(() => {
    void load()
  }, [load, segmentId])

  const selected = useMemo(
    () => briefs.find((b) => b.id === selectedId) ?? null,
    [briefs, selectedId],
  )

  const handleGenerate = async () => {
    setGenerating(true)
    setError(null)
    try {
      const brief = await onGenerate()
      setBriefs((prev) => [...prev.map((b) => ({ ...b, isCurrent: false })), brief])
      setSelectedId(brief.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  if (loading && briefs.length === 0) {
    return (
      <section className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        Loading brief…
      </section>
    )
  }

  if (briefs.length === 0) {
    return (
      <section
        className="rounded-xl border border-dashed p-6 text-center"
        style={{
          borderColor: 'var(--color-border-subtle)',
          backgroundColor: 'var(--color-warm-50)',
        }}
      >
        <div
          className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--color-warm-100)' }}
        >
          <BookOpen className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
        </div>
        <h3 className="font-display text-sm font-semibold text-[var(--color-text)]">
          Market brief
        </h3>
        <p
          className="mx-auto mt-1 max-w-md text-xs"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          AI-generated strategic brief across nine sections. Regenerable with full version
          history.
        </p>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-50"
          style={{ backgroundColor: 'var(--color-market)' }}
        >
          <Sparkles className="h-3 w-3" />
          {generating ? 'Generating…' : 'Generate brief'}
        </button>
        {error ? (
          <p className="mt-2 text-xs" style={{ color: '#b14132' }}>
            {error}
          </p>
        ) : null}
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
            Market brief
          </h3>
          <VersionDropdown
            briefs={briefs}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-[var(--color-warm-100)] disabled:opacity-50"
          style={{
            borderColor: 'var(--color-border-subtle)',
            color: 'var(--color-text)',
          }}
          title="Regenerate — previous versions are preserved"
        >
          <Sparkles className="h-3 w-3" style={{ color: 'var(--color-market)' }} />
          {generating ? 'Regenerating…' : 'Regenerate'}
        </button>
      </header>
      {error ? (
        <p className="text-xs" style={{ color: '#b14132' }}>
          {error}
        </p>
      ) : null}
      {selected ? <BriefBody brief={selected} /> : null}
    </section>
  )
}

function BriefBody({ brief }: { brief: MarketBrief }) {
  return (
    <div className="space-y-3">
      {BRIEF_SECTIONS.map((key) => {
        const text = brief.content[key]
        if (!text) return null
        return <BriefSectionBlock key={key} title={SECTION_LABELS[key]} text={text} />
      })}
    </div>
  )
}

function BriefSectionBlock({ title, text }: { title: string; text: string }) {
  const [open, setOpen] = useState(true)
  return (
    <div
      className="rounded-lg border"
      style={{ borderColor: 'var(--color-border-subtle)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-[var(--color-warm-50)]"
      >
        <span
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {title}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 transition-transform"
          style={{
            color: 'var(--color-text-muted)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open ? (
        <p
          className="whitespace-pre-wrap px-4 pb-3 pt-0 text-sm leading-relaxed"
          style={{ color: 'var(--color-text)' }}
        >
          {text}
        </p>
      ) : null}
    </div>
  )
}

function VersionDropdown({
  briefs,
  selectedId,
  onSelect,
}: {
  briefs: MarketBrief[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const current = briefs.find((b) => b.id === selectedId)
  if (!current) return null
  return (
    <div className="relative">
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
        className="appearance-none rounded-md border pl-2 pr-7 py-1 text-xs"
        style={{
          borderColor: 'var(--color-border-subtle)',
          backgroundColor: 'var(--color-warm-50)',
          color: 'var(--color-text)',
        }}
        aria-label="Brief version"
      >
        {[...briefs]
          .sort((a, b) => b.version - a.version)
          .map((b) => (
            <option key={b.id} value={b.id}>
              v{b.version}
              {b.isCurrent ? ' · current' : ''} · {new Date(b.generatedAt).toLocaleDateString()}
            </option>
          ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2"
        style={{ color: 'var(--color-text-muted)' }}
      />
    </div>
  )
}
